import { getSchedule, type Schedule } from "@aura-frontend/omnibus-az-client";
import { useQueries, useQuery } from "@tanstack/react-query";
import { isPopulatedArray } from "@win-reality/react-ui";
import { useMemo } from "react";

import type { Complex } from "@/types/game";
import { isDeletedSchedule } from "@/utils/scheduleUtils";

import { isDefined } from "../../../Tagging/utils/isDefined";

export const SCHEDULES_QUERY_KEY = "schedules" as const;

const PAGE_COUNT = 1000;

/**
 * Full month window, in epoch seconds. `month` is 0-indexed (JS Date convention).
 */
export function getFullMonthWindow(
  year: number,
  month: number,
): { startTime: number; endTime: number } {
  const start = new Date(year, month, 1, 0, 0, 0);
  const end = new Date(year, month + 1, 0, 23, 59, 59);
  return {
    startTime: Math.floor(start.getTime() / 1000),
    endTime: Math.floor(end.getTime() / 1000),
  };
}

/**
 * Convert a "months from now" offset to absolute `{ year, month }` (0-indexed).
 */
export const getMonthForOffset = (
  offset: number,
): { year: number; month: number } => {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  return { year: d.getFullYear(), month: d.getMonth() };
};

/**
 * Shared React Query options factory for fetching schedules in a single month
 * window across one or more fields. Every caller — Upcoming sections, the
 * EventSchedule list, the FieldCalendar grid, the per-complex count — keys
 * off the same `[SCHEDULES_QUERY_KEY, fieldIds, year, month]` so requests for
 * the same field set + month dedupe in the cache.
 *
 * `refetchIntervalMs` is per-observer (react-query attaches it to the
 * subscribing observer, not the cached query), so callers that want polling
 * (e.g. the SingleField page) can opt in without forcing it on the multi-field
 * consumers (SingleComplex counts, EventSchedule).
 */
export const scheduleMonthQueryOptions = (
  fieldIds: string[],
  year: number,
  month: number,
  refetchIntervalMs?: number,
) => ({
  queryKey: [SCHEDULES_QUERY_KEY, fieldIds, year, month],
  enabled: isPopulatedArray(fieldIds),
  refetchInterval: refetchIntervalMs,
  queryFn: async (): Promise<Schedule[]> => {
    const { startTime, endTime } = getFullMonthWindow(year, month);
    const results = await Promise.all(
      fieldIds.map(async (machineUuid) => {
        const { data } = await getSchedule({
          query: { machineUuid, startTime, endTime, count: PAGE_COUNT },
          throwOnError: true,
        });
        return data;
      }),
    );
    const schedules: Schedule[] = [];
    for (const result of results) {
      if (result?.continue) {
        throw new Error(
          "Unexpected pagination cursor from schedule API — increase PAGE_COUNT",
        );
      }
      schedules.push(
        ...(result?.schedules ?? []).filter((s) => !isDeletedSchedule(s)),
      );
    }
    schedules.sort((a, b) => a.schedule_time_s - b.schedule_time_s);
    return schedules;
  },
});

/**
 * Fetch schedules for a single month (offset N from the current month).
 * Optionally filter out events that have already started (`fromNow`).
 */
export const useScheduledEventsForMonth = ({
  fieldIds,
  monthOffset = 0,
  maxResults,
  fromNow = false,
}: {
  fieldIds: string[];
  monthOffset?: number;
  maxResults?: number;
  fromNow?: boolean;
}) => {
  const enabled = isPopulatedArray(fieldIds);
  const { year, month } = useMemo(
    () => getMonthForOffset(monthOffset),
    [monthOffset],
  );

  const { data, isLoading } = useQuery(
    scheduleMonthQueryOptions(fieldIds, year, month),
  );

  const filtered = useMemo(() => {
    const all = data ?? [];
    if (!fromNow) return all;
    const nowS = Math.floor(Date.now() / 1000);
    return all.filter((s) => s.schedule_time_s >= nowS);
  }, [data, fromNow]);

  const events = maxResults != null ? filtered.slice(0, maxResults) : filtered;

  return {
    events,
    isLoading,
    isInitialLoading: enabled && isLoading,
  };
};

/**
 * Fetch schedules across a contiguous range of months starting at the current
 * month (offset 0) and extending forward `monthCount` months. Each month is
 * fetched via the same `scheduleMonthQueryOptions` factory the single-month
 * hook uses, so cache entries are shared with calendar / count / list views.
 */
export const useScheduledEventsForMonthRange = ({
  fieldIds,
  monthCount,
  fromNow = false,
  refetchIntervalMs,
}: {
  fieldIds: string[];
  monthCount: number;
  fromNow?: boolean;
  refetchIntervalMs?: number;
}) => {
  const months = useMemo(
    () =>
      Array.from({ length: Math.max(1, monthCount) }, (_, i) =>
        getMonthForOffset(i),
      ),
    [monthCount],
  );

  const queries = useQueries({
    queries: months.map(({ year, month }) =>
      scheduleMonthQueryOptions(fieldIds, year, month, refetchIntervalMs),
    ),
  });

  const dataSignature = queries.map((q) => q.data?.length ?? -1).join("|");
  const events = useMemo(() => {
    const all = queries.flatMap((q) => q.data ?? []);
    all.sort((a, b) => a.schedule_time_s - b.schedule_time_s);
    if (!fromNow) return all;
    const nowS = Math.floor(Date.now() / 1000);
    return all.filter((s) => s.schedule_time_s >= nowS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSignature, fromNow]);

  const isInitialLoading = queries[0]?.isLoading ?? false;
  const isFetchingMore = queries.slice(1).some((q) => q.isLoading);

  return { events, isInitialLoading, isFetchingMore };
};

export const useComplexesScheduledEventsCount = (
  complexes: Complex[] | undefined,
) => {
  const { year, month } = useMemo(() => getMonthForOffset(0), []);

  const perComplexFieldIds = useMemo(
    () =>
      (complexes ?? []).map((complex) =>
        complex.fields.map((f) => f.machineUuid).filter(isDefined),
      ),
    [complexes],
  );

  const queries = useQueries({
    queries: perComplexFieldIds.map((fieldIds) =>
      scheduleMonthQueryOptions(fieldIds, year, month),
    ),
  });

  return useMemo(() => {
    const nowS = Math.floor(Date.now() / 1000);
    const map = new Map<string, number | undefined>();
    (complexes ?? []).forEach((complex, idx) => {
      const data = queries[idx]?.data;
      map.set(
        complex.name,
        data ? data.filter((s) => s.schedule_time_s >= nowS).length : undefined,
      );
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complexes, queries.map((q) => q.data?.length ?? -1).join("|")]);
};
